// Alert-channel detail Drawer (docs/09 §4/§7: detail drawers, no per-object
// route). Subject rides `?channel=<id>`; the drawer owns its detail query,
// the delete mutation stays in the route container (success closes the
// drawer, failure keeps the §2.12 confirm open).

import { useEffect, useRef, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError } from "../../lib/errors";
import { formatDateTime } from "../../lib/format";
import { queryKeys } from "../../lib/queryKeys";
import {
  Button,
  Code,
  Drawer,
  InlineError,
  KeyValue,
  Skeleton,
  Stack,
  Text,
  type KeyValueRow,
} from "../../ui";
import { RowConfirm } from "../shared/RowConfirm";
import { AlertChannelForm } from "./AlertChannelForm";

type AlertChannel = components["schemas"]["AlertChannelResponse"];
type AlertChannelPatch = components["schemas"]["AlertChannelPatch"];
type AlertChannelType = components["schemas"]["AlertChannelType"];

// Secret-bearing fields render only the server-projected fixed hint. The
// plaintext credential never reaches this read surface.
function channelConfigRows(channel: AlertChannel): KeyValueRow[] {
  const config = channel.config;
  if (config == null) {
    return [];
  }
  if ("channel_id" in config) {
    return [
      {
        label: "Workspace channel",
        value: `#${config.channel_name}`,
      },
    ];
  }
  if ("to_addresses" in config) {
    return [
      { label: "From identity", value: config.from_identity },
      { label: "To addresses", value: config.to_addresses.join(", ") },
    ];
  }
  if ("routing_key_hint" in config) {
    return [
      {
        label: "Routing key",
        value: config.routing_key_hint,
      },
    ];
  }
  if ("alert_source_config_id" in config) {
    return [
      {
        label: "Alert source config ID",
        value: <Code>{config.alert_source_config_id}</Code>,
      },
      { label: "Authentication", value: config.auth_mode },
      {
        label: "Token secret ref",
        value: config.token_secret_ref_hint,
      },
    ];
  }
  return [
    { label: "Webhook URL", value: config.url_hint },
    {
      label: "Signing secret",
      value: config.signing_secret_hint ?? "none",
    },
    {
      label: "Headers",
      value:
        config.headers == null
          ? "none"
          : Object.entries(config.headers)
              .map(([name, value]) => `${name}: ${value}`)
              .join(" · "),
    },
  ];
}

function channelDetailRows(channel: AlertChannel): KeyValueRow[] {
  return [
    ...channelConfigRows(channel),
    {
      label: "Created",
      value:
        channel.created_by == null
          ? formatDateTime(channel.created_at)
          : `${formatDateTime(channel.created_at)} by ${channel.created_by}`,
    },
    { label: "Updated", value: formatDateTime(channel.updated_at) },
  ];
}

export interface AlertChannelDrawerViewProps {
  availableChannelTypes: AlertChannelType[];
  canEdit: boolean;
  channel?: AlertChannel;
  editErrorMessage?: string;
  errorMessage?: string;
  isDeletePending: boolean;
  isEditPending: boolean;
  isPending: boolean;
  onDelete: () => void;
  onDismiss: () => void;
  onEdit: (body: AlertChannelPatch) => Promise<void>;
  onEditReset: () => void;
}

export function AlertChannelDrawerView({
  availableChannelTypes,
  canEdit,
  channel,
  editErrorMessage,
  errorMessage,
  isDeletePending,
  isEditPending,
  isPending,
  onDelete,
  onDismiss,
  onEdit,
  onEditReset,
}: AlertChannelDrawerViewProps) {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const editRequestRef = useRef<Promise<void> | null>(null);
  const isEditLocked = isEditPending || isEditSubmitting;

  useEffect(() => {
    setIsEditing(false);
  }, [channel?.id]);

  return (
    <Drawer
      ariaLabel="Alert channel detail"
      isOpen
      onDismiss={() => {
        if (!isEditLocked) {
          onDismiss();
        }
      }}
    >
      {isPending ? (
        <Skeleton label="Loading alert channel" rows={4} shape="detail-field" />
      ) : null}
      {errorMessage != null ? <InlineError>{errorMessage}</InlineError> : null}
      {channel && isEditing ? (
        <AlertChannelForm
          availableChannelTypes={availableChannelTypes}
          channel={channel}
          errorMessage={editErrorMessage}
          isPending={isEditLocked}
          onCancel={() => {
            if (isEditLocked) {
              return;
            }
            onEditReset();
            setIsEditing(false);
          }}
          onSubmit={(body) => {
            if (editRequestRef.current != null) {
              return;
            }
            setIsEditSubmitting(true);
            const request = onEdit(body);
            editRequestRef.current = request;
            void request
              .then(
                () => {
                  setIsEditing(false);
                },
                () => undefined,
              )
              .finally(() => {
                if (editRequestRef.current === request) {
                  editRequestRef.current = null;
                  setIsEditSubmitting(false);
                }
              });
          }}
          submitLabel="Save channel"
        />
      ) : channel ? (
        <Stack gap={5}>
          <Stack gap={1}>
            <Text>{channel.display_name}</Text>
            <Text variant="muted">{channel.channel_type}</Text>
          </Stack>
          <KeyValue rows={channelDetailRows(channel)} />
          {canEdit ? (
            <>
              <Button
                disabled={isEditLocked}
                type="button"
                onClick={() => {
                  onEditReset();
                  setIsEditing(true);
                }}
              >
                Edit channel
              </Button>
              <RowConfirm
                confirmLabel="Confirm delete"
                isConfirming={isConfirmingDelete}
                isPending={isDeletePending}
                onCancel={() => {
                  setIsConfirmingDelete(false);
                }}
                onConfirm={onDelete}
                onStart={() => {
                  setIsConfirmingDelete(true);
                }}
                pendingLabel="Deleting..."
                sentence={`Delete alert channel ${channel.display_name}? This removes the delivery target.`}
                triggerLabel="Delete channel"
              />
            </>
          ) : null}
        </Stack>
      ) : null}
    </Drawer>
  );
}

export interface AlertChannelDrawerProps {
  availableChannelTypes: AlertChannelType[];
  canEdit: boolean;
  channelId: string;
  editErrorMessage?: string;
  isDeletePending: boolean;
  isEditPending: boolean;
  onDelete: () => void;
  onDismiss: () => void;
  onEdit: (body: AlertChannelPatch) => Promise<void>;
  onEditReset: () => void;
}

export function AlertChannelDrawer({
  availableChannelTypes,
  canEdit,
  channelId,
  editErrorMessage,
  isDeletePending,
  isEditPending,
  onDelete,
  onDismiss,
  onEdit,
  onEditReset,
}: AlertChannelDrawerProps) {
  // 404 on a load-time deep link → drawer error state, param kept until
  // dismiss; delete success closes the drawer before any refetch could 404.
  const channel = useQuery({
    queryKey: queryKeys.alertChannels.detail(channelId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/alert-channels/{id}", {
        params: { path: { id: channelId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  return (
    <AlertChannelDrawerView
      availableChannelTypes={availableChannelTypes}
      canEdit={canEdit}
      channel={channel.data}
      editErrorMessage={editErrorMessage}
      errorMessage={channel.isError ? channel.error.message : undefined}
      isDeletePending={isDeletePending}
      isEditPending={isEditPending}
      isPending={channel.isPending}
      onDelete={onDelete}
      onDismiss={onDismiss}
      onEdit={onEdit}
      onEditReset={onEditReset}
    />
  );
}
